import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/cn';
import { LedgerInput, LedgerSelect } from '../ui';
import { Setting, SettingDataType } from '../../types/settings.types';

interface SettingFieldProps {
  setting: Setting;
  value: any;
  onChange: (key: string, value: any) => void;
  error?: string;
  disabled?: boolean;
}

const SettingField: React.FC<SettingFieldProps> = ({
  setting,
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: any) => {
    setLocalValue(newValue);
    onChange(setting.key, newValue);
  };

  const handleReset = () => {
    handleChange(setting.default_value);
  };

  const renderField = () => {
    if (setting.data_type === SettingDataType.PASSWORD || setting.is_sensitive) {
      return (
        <LedgerInput
          id={setting.key}
          revealToggle
          value={localValue ?? ''}
          invalid={!!error}
          disabled={disabled}
          onChange={(event) => handleChange(event.target.value)}
        />
      );
    }

    switch (setting.data_type) {
      case SettingDataType.BOOLEAN: {
        const on = localValue === 'true' || localValue === true;
        return (
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={setting.display_name}
            disabled={disabled}
            onClick={() => handleChange((!on).toString())}
            className="flex cursor-pointer items-center gap-2 disabled:cursor-default"
          >
            <span aria-hidden className={cn('h-3 w-3 border border-ink', on && 'bg-ink')} />
            <span className="text-scale-1 text-ink">{on ? 'Enabled' : 'Disabled'}</span>
          </button>
        );
      }

      case SettingDataType.INTEGER:
      case SettingDataType.DECIMAL:
        return (
          <LedgerInput
            id={setting.key}
            type="number"
            value={localValue ?? ''}
            min={setting.min_value}
            max={setting.max_value}
            step={setting.data_type === SettingDataType.DECIMAL ? 0.01 : 1}
            invalid={!!error}
            disabled={disabled}
            onChange={(event) => handleChange(event.target.value)}
          />
        );

      case SettingDataType.COLOR:
        return (
          <div className="flex items-center gap-2">
            <LedgerInput
              id={setting.key}
              value={localValue ?? ''}
              invalid={!!error}
              disabled={disabled}
              onChange={(event) => handleChange(event.target.value)}
            />
            <input
              type="color"
              aria-label={`${setting.display_name} swatch`}
              value={String(localValue || '#000000')}
              onChange={(event) => handleChange(event.target.value)}
              disabled={disabled}
              className="h-8 w-10 cursor-pointer border border-rule-faint bg-transparent"
            />
          </div>
        );

      case SettingDataType.JSON:
        return (
          <textarea
            id={setting.key}
            rows={4}
            value={typeof localValue === 'string' ? localValue : JSON.stringify(localValue, null, 2)}
            onChange={(event) => handleChange(event.target.value)}
            disabled={disabled}
            className="w-full border border-rule-faint bg-transparent p-2 font-mono text-scale-0 text-ink outline-none focus:border-accent"
          />
        );

      default: {
        // STRING, EMAIL, URL
        if (setting.choices && setting.choices.length > 0) {
          return (
            <LedgerSelect
              ariaLabel={setting.display_name}
              value={String(localValue ?? '')}
              options={setting.choices.map((choice) => ({ value: String(choice), label: String(choice) }))}
              onValueChange={(next) => handleChange(next)}
              disabled={disabled}
            />
          );
        }

        return (
          <LedgerInput
            id={setting.key}
            type={setting.data_type === SettingDataType.EMAIL ? 'email' : 'text'}
            value={localValue ?? ''}
            invalid={!!error}
            disabled={disabled}
            onChange={(event) => handleChange(event.target.value)}
          />
        );
      }
    }
  };

  // localValue seeds from typed_value (real booleans/numbers/arrays) while
  // default_value is always the raw string — normalize before comparing, or
  // untouched boolean/number/JSON rows show a spurious reset affordance.
  const normalize = (input: unknown): string => {
    if (input == null) return '';
    if (typeof input === 'string') return input;
    return typeof input === 'object' ? JSON.stringify(input) : String(input);
  };
  const matchesDefault = (() => {
    const local = normalize(localValue);
    const fallback = normalize(setting.default_value);
    if (local === fallback) return true;
    if (setting.data_type === SettingDataType.JSON) {
      try {
        return JSON.stringify(JSON.parse(local)) === JSON.stringify(JSON.parse(fallback));
      } catch {
        return false;
      }
    }
    return false;
  })();

  return (
    <div className="border-b border-rule-faint py-3 md:grid md:grid-cols-[280px_1fr] md:gap-x-6">
      <div>
        <label htmlFor={setting.key} className="block text-scale-1 font-medium text-ink">
          {setting.display_name}
        </label>
        {setting.description && <p className="mt-0.5 text-scale-0 text-ink-muted">{setting.description}</p>}
        {setting.requires_restart && <p className="mt-0.5 text-scale-0 text-sev-high">* Requires restart</p>}
      </div>
      <div className="mt-2 md:mt-0">
        {renderField()}
        {error && <p className="mt-0.5 text-scale-0 text-sev-critical">{error}</p>}
        {!matchesDefault && !disabled && (
          <button type="button" onClick={handleReset} className="cap-label mt-1 cursor-pointer hover:!text-ink">
            Reset to default
          </button>
        )}
      </div>
    </div>
  );
};

export default SettingField;
